import { useNavigate } from "react-router-dom";
import { Lock, Flame, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PRICING, IS_PRELAUNCH } from "@/lib/pricing";

const QUICK_FEATURES = [
  "Unlimited rewrites (you're on 3/day free)",
  "Conflict Score tracking",
  "Real-time warning system",
  "Saved message history",
];

export default function PreLaunchUpgradePrompt({ onClose }) {
  const navigate = useNavigate();

  const goToPricing = () => {
    navigate("/pricing");
    onClose?.();
  };

  return (
    <div className="bg-white rounded-3xl shadow-2xl overflow-hidden relative" data-testid="upgrade-prompt">
      {/* Close */}
      {onClose && (
        <button
          onClick={onClose}
          data-testid="upgrade-prompt-close"
          className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-black/5 hover:bg-black/10 flex items-center justify-center text-[#6B7280]"
        >
          <X size={16} />
        </button>
      )}

      {/* Header */}
      <div
        className="px-6 pt-8 pb-6 text-white relative"
        style={{
          background:
            "linear-gradient(135deg, #1F3A5F 0%, #2a4a75 50%, #4FB3A5 100%)",
        }}
      >
        <div className="w-14 h-14 rounded-2xl bg-white/15 backdrop-blur-sm flex items-center justify-center mb-4 border border-white/20">
          <Lock className="text-white" size={26} />
        </div>
        <h2 className="text-2xl font-bold leading-tight mb-1" data-testid="upgrade-prompt-title">
          You've hit today's free limit
        </h2>
        <p className="text-white/80 text-sm leading-relaxed">
          You get 3 free checks per day. Upgrade to PRO for unlimited rewrites.
        </p>
      </div>

      {/* Body */}
      <div className="px-6 py-5">
        {/* Features peek */}
        <ul className="space-y-2 mb-5">
          {QUICK_FEATURES.map((f, i) => (
            <li key={i} className="flex items-center gap-2 text-sm text-[#2C2C2C]">
              <Check size={14} className="text-[#4FB3A5] flex-shrink-0" />
              <span>{f}</span>
            </li>
          ))}
        </ul>

        {/* Offer highlight */}
        <div
          className="rounded-2xl p-4 mb-4 border-2 border-[#FEB95A]/40"
          style={{
            background:
              "linear-gradient(135deg, #FFF8EB 0%, #FFE7B8 100%)",
          }}
          data-testid="upgrade-prompt-offer"
        >
          <div className="flex items-center gap-1.5 text-[10px] font-bold text-[#8B4A00] uppercase tracking-wider mb-1">
            <Flame size={12} />
            {PRICING.promo.badge}
          </div>
          <p className="text-[#1F3A5F] text-sm font-bold leading-tight mb-1">
            Limited time: Get 1 year for {PRICING.yearly.price}
          </p>
          <p className="text-[#6B7280] text-xs">
            vs <span className="font-semibold">{PRICING.monthly.label}</span> standard —
            {" "}{IS_PRELAUNCH ? "no charge today, activate at app launch" : PRICING.yearly.perMonth}
          </p>
        </div>

        {/* CTAs */}
        <Button
          onClick={goToPricing}
          className="w-full py-5 rounded-xl bg-[#1F3A5F] hover:bg-[#2a4a75] text-white font-bold shadow-md"
          data-testid="upgrade-prompt-cta"
        >
          {IS_PRELAUNCH ? "Reserve $5.99 deal" : "Upgrade Now"}
        </Button>
        <button
          onClick={onClose}
          className="w-full text-center text-xs text-[#A0A7B4] hover:text-[#6B7280] mt-3 py-1"
          data-testid="upgrade-prompt-dismiss"
        >
          Your free checks reset in 24 hours
        </button>
      </div>
    </div>
  );
}
